/**
 * Utility functions for handling business settings with safe defaults
 */
// IMPORTANT: Import the configured api client instead of axios directly
import api from '../services/api';
import { calculateSupportStaffCostPerMinute } from './supportStaffCostCalculator';

/**
 * Loads business settings from the API
 * @returns {Promise<Object>} The business settings
 */
export const loadBusinessSettings = async () => {
  try {
    // FIXED: Remove /api prefix since api.js automatically adds it
    console.log('Loading business settings from /business/settings...');
    const response = await api.get('/business/settings');
    
    if (!response || !response.data) {
      console.error('No data returned from API');
      return {};
    }
    
    // Process the response to ensure calculated values are set
    const settings = response.data;
    console.log('Raw settings from API:', settings);
    
    // FIXED: Properly map backend fields to frontend fields
    const normalizedSettings = {
      // Map backend fields to frontend field names
      monthly_profit_target: settings.monthly_profit_target || settings.monthly_profit || '',
      annual_overhead: settings.total_annual_overhead || settings.annual_overhead || 0,
      weeks_per_year: settings.weeks_per_year || '52',
      staff_utilization_rate: settings.staff_utilization_rate || '',
      trading_hours: settings.trading_hours || null,
      total_working_days: settings.total_working_days || 0,
      total_working_hours: settings.weekly_working_hours || settings.total_working_hours || 0,
      profit_level_1: settings.default_profit_margin || settings.profit_level_1 || '',
      profit_level_2: settings.profit_level_2 || '',
      vat_rate: settings.default_vat_rate || settings.vat_rate || '20',
      is_vat_registered: settings.is_vat_registered || false,
      
      // Calculated fields (keep these as-is from backend)
      annual_profit_target: settings.annual_profit || 0,
      weekly_profit_target: settings.weekly_target_profit || 0,
      overhead_per_minute: settings.overhead_per_minute || 0,
      owner_profit_per_minute: settings.owner_profit_per_minute || 0,
      support_staff_per_minute: settings.support_staff_per_minute || 0,
      total_annual_hours: settings.annual_hours || 0,
      effective_hours: settings.effective_weekly_hours ? parseFloat(settings.effective_weekly_hours) * parseFloat(settings.weeks_per_year || 52) : 0
    };
    
    console.log('Normalized settings:', normalizedSettings);
    
    // CRITICAL FIX: If monthly_profit_target is missing but we have annual_profit, calculate it
    if (!normalizedSettings.monthly_profit_target && normalizedSettings.annual_profit_target) {
      normalizedSettings.monthly_profit_target = (parseFloat(normalizedSettings.annual_profit_target) / 12).toFixed(2);
      console.log('Calculated monthly_profit_target from annual:', normalizedSettings.monthly_profit_target);
    }
    
    // Calculate missing profit_level_2 if we have profit_level_1
    if (!normalizedSettings.profit_level_2 && normalizedSettings.profit_level_1) {
      normalizedSettings.profit_level_2 = Math.min(parseFloat(normalizedSettings.profit_level_1) * 1.5, 100).toFixed(0);
      console.log('Calculated profit_level_2:', normalizedSettings.profit_level_2);
    }
    
    console.log('Final processed settings:', normalizedSettings);
    return normalizedSettings || {};
  } catch (error) {
    console.error('Error loading business settings:', error);
    // Return empty object instead of default values
    return {};
  }
};

/**
 * Saves business settings to the API - FIXED ENDPOINT
 * @param {Object} settings - The business settings to save
 * @returns {Promise<Object>} The saved business settings
 */
export const saveBusinessSettings = async (settings) => {
  try {
    // Create a copy of settings to format values
    const formattedSettings = { ...settings };
    
    // CRITICAL FIX: Map frontend field names to backend field names
    // Your backend expects these exact field names based on the Python code
    const backendSettings = {
      // Direct mappings that match your Python backend
      monthly_profit_target: formattedSettings.monthly_profit_target || formattedSettings.monthly_profit || '0',
      weekly_target_profit: formattedSettings.weekly_profit_target || '0',
      annual_profit: formattedSettings.annual_profit_target || '0',
      
      // Staff and time settings
      staff_utilization_rate: formattedSettings.staff_utilization_rate || '80',
      weeks_per_year: formattedSettings.weeks_per_year || '52',
      weekly_working_hours: formattedSettings.total_working_hours || '40',
      
      // Financial settings
      hourly_rate: formattedSettings.hourly_rate || '50',
      overhead_per_minute: formattedSettings.overhead_per_minute || '0',
      support_staff_per_minute: formattedSettings.support_staff_per_minute || '0',
      
      // Profit and VAT
      default_profit_margin: formattedSettings.profit_level_1 || '10',
      default_vat_rate: formattedSettings.vat_rate || '20',
      is_vat_registered: formattedSettings.is_vat_registered || false,
      
      // Overhead
      total_annual_overhead: formattedSettings.annual_overhead || '0'
    };
    
    // FIXED: Format values based on what Python backend expects
    const integerFields = ['weeks_per_year']; // Fields that must be integers
    const decimalFields = ['monthly_profit_target', 'weekly_target_profit', 'annual_profit', 'staff_utilization_rate', 
                          'weekly_working_hours', 'hourly_rate', 'overhead_per_minute', 'support_staff_per_minute',
                          'default_profit_margin', 'default_vat_rate', 'total_annual_overhead'];
    
    Object.keys(backendSettings).forEach(key => {
      if (key !== 'is_vat_registered' && backendSettings[key] !== null && backendSettings[key] !== undefined) {
        const numValue = parseFloat(backendSettings[key]) || 0;
        
        if (integerFields.includes(key)) {
          // Send as integer for fields that Python expects as int
          backendSettings[key] = Math.round(numValue);
        } else if (decimalFields.includes(key)) {
          // Send as number (not string) for Decimal fields
          if (key.includes('per_minute')) {
            backendSettings[key] = parseFloat(numValue.toFixed(6));
          } else if (key.includes('rate') || key.includes('margin')) {
            backendSettings[key] = parseFloat(numValue.toFixed(2));
          } else {
            backendSettings[key] = parseFloat(numValue.toFixed(2));
          }
        }
      }
    });
    
    console.log('💾 Saving business settings (frontend → backend mapping):', backendSettings);
    
    const timestamp = Date.now();
    // FIXED: Remove /api prefix since api.js automatically adds it
    const endpoint = `/business/settings?t=${timestamp}&user_id=1`;
    
    console.log(`📤 Posting to: ${endpoint}`);
    
    // ENHANCED: Better error handling and response checking
    try {
      const response = await api.post(endpoint, backendSettings);
      
      console.log(`📥 Response:`, response.status || 'Success');
      console.log('✅ Save response:', response.data || response);
      
      // ENHANCED: Check response content type before processing
      if (response && response.headers) {
        const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
        console.log('📄 Response Content-Type:', contentType);
        
        if (contentType.includes('text/html')) {
          console.error('🚨 Server returned HTML instead of JSON!');
          throw new Error('Server returned HTML page instead of JSON. This usually indicates a server error or wrong endpoint.');
        }
      }
      
      return response.data || response;
      
    } catch (apiError) {
      console.error('❌ API Error Details:', apiError);
      
      // ENHANCED: Better error analysis and reporting
      if (apiError.response) {
        console.error('Response Status:', apiError.response.status);
        console.error('Response Headers:', apiError.response.headers);
        
        // Try to get response text for HTML errors
        if (apiError.response.data) {
          if (typeof apiError.response.data === 'string' && apiError.response.data.includes('<!doctype')) {
            console.error('🚨 HTML Response detected:', apiError.response.data.substring(0, 200) + '...');
            throw new Error('Server returned HTML error page. Check backend logs for the actual error.');
          }
          console.error('Response Data:', apiError.response.data);
        }
        
        // Check content type in headers
        const contentType = apiError.response.headers?.['content-type'] || '';
        if (contentType.includes('text/html')) {
          throw new Error('Server returned HTML page instead of JSON. Check backend logs for errors.');
        }
        
        // Handle specific error codes
        if (apiError.response.status === 404) {
          throw new Error('API endpoint not found. Check if /api/business/settings POST route exists.');
        } else if (apiError.response.status === 500) {
          throw new Error('Server error. Check backend logs for details.');
        } else if (apiError.response.status === 401 || apiError.response.status === 403) {
          throw new Error('Authentication failed. Please refresh and try again.');
        } else if (apiError.response.status === 422) {
          throw new Error('Invalid data sent to server. Check field formats.');
        }
      } else if (apiError.request) {
        // Network error
        console.error('Network Error - No response received:', apiError.request);
        throw new Error('Network error - no response from server. Check your connection.');
      }
      
      // Re-throw the original error if we can't categorize it
      throw apiError;
    }
    
  } catch (error) {
    console.error('❌ Error saving business settings:', error);
    
    // Provide user-friendly error messages
    if (error.message.includes('Network Error')) {
      throw new Error('Network connection failed. Please check your internet connection.');
    } else if (error.message.includes('timeout')) {
      throw new Error('Request timed out. Please try again.');
    } else if (error.message.includes('<!doctype') || error.message.includes('Unexpected token')) {
      throw new Error('Server configuration error. The backend returned HTML instead of JSON data.');
    }
    
    throw error;
  }
};

// Enhanced test function with better diagnostics
export const testBusinessSettingsAPI = async () => {
  try {
    console.log('🧪 Testing business settings API...');
    
    // Test GET first - FIXED: Remove /api prefix
    console.log('📥 Testing GET /business/settings...');
    const getResponse = await api.get('/business/settings');
    console.log('✅ GET works:', getResponse.status);
    console.log('📄 GET Content-Type:', getResponse.headers?.['content-type'] || 'unknown');
    
    // Test POST with minimal data - FIXED: Use correct endpoint
    console.log('📤 Testing POST /api/business/settings...');
    const testData = {
      monthly_profit_target: 1000,
      weeks_per_year: 52,
      staff_utilization_rate: 80
    };
    
    const postResponse = await api.post('/api/business/settings', testData);
    console.log('✅ POST works:', postResponse.status);
    console.log('📄 POST Content-Type:', postResponse.headers?.['content-type'] || 'unknown');
    
    return true;
  } catch (error) {
    console.error('❌ API Test failed:', error);
    
    if (error.response) {
      console.error('❌ Test Response Status:', error.response.status);
      console.error('❌ Test Response Headers:', error.response.headers);
      console.error('❌ Test Response Data:', error.response.data);
    }
    
    return false;
  }
};

/**
 * Calculates per-minute rate from annual amount and hours
 * @param {number|string} annualAmount - Annual amount (e.g. £30,000)
 * @param {number|string} hours - Annual hours (e.g. 1,800)
 * @returns {number} Per-minute rate as a number with high precision
 */
export const calculatePerMinuteRate = (annualAmount, hours) => {
  if (!annualAmount || !hours) return 0;
  
  const annualAmountNum = parseFloat(annualAmount);
  const hoursNum = parseFloat(hours);
  
  if (isNaN(annualAmountNum) || isNaN(hoursNum) || hoursNum === 0) return 0;
  
  const minutesPerYear = hoursNum * 60;
  return annualAmountNum / minutesPerYear;
};

/**
 * Calculates annual amount from per-minute rate and hours
 * @param {number|string} perMinuteRate - Per-minute rate (e.g. £0.27)
 * @param {number|string} hours - Annual hours (e.g. 1,800)
 * @returns {number} Annual amount as a number
 */
export const calculateAnnualAmount = (perMinuteRate, hours) => {
  if (!perMinuteRate || !hours) return 0;
  
  const rateNum = parseFloat(perMinuteRate);
  const hoursNum = parseFloat(hours);
  
  if (isNaN(rateNum) || isNaN(hoursNum)) return 0;
  
  const minutesPerYear = hoursNum * 60;
  return rateNum * minutesPerYear;
};

/**
 * Validates business settings to ensure all required fields are present
 * @param {Object} settings - The business settings to validate
 * @returns {Object} Object with isValid and errors properties
 */
export const validateBusinessSettings = (settings) => {
  const errors = {};
  
  // Check for required fields
  if (!settings.weekly_working_hours) {
    errors.weekly_working_hours = 'Weekly working hours is required';
  }
  
  if (!settings.weeks_per_year) {
    errors.weeks_per_year = 'Weeks per year is required';
  }
  
  if (!settings.monthly_profit_target && !settings.annual_profit) {
    errors.monthly_profit_target = 'Monthly profit target is required';
  }
  
  // Calculate missing values if possible to avoid validation errors
  if (settings.weekly_working_hours && settings.weeks_per_year) {
    settings.annual_hours = settings.weekly_working_hours * settings.weeks_per_year;
  }
  
  if (settings.monthly_profit_target) {
    settings.annual_profit = settings.monthly_profit_target * 12;
  }
  
  // Ensure both field names for annual overhead are set
  if (settings.annual_overhead) {
    settings.total_annual_overhead = settings.annual_overhead;
  } else if (settings.total_annual_overhead) {
    settings.annual_overhead = settings.total_annual_overhead;
  }
  
  // Calculate per-minute rates if possible
  if (!settings.owner_profit_per_minute && settings.annual_profit && settings.annual_hours) {
    settings.owner_profit_per_minute = calculatePerMinuteRate(
      settings.annual_profit,
      settings.annual_hours
    );
  }
  
  if (!settings.overhead_per_minute && settings.annual_overhead && settings.annual_hours) {
    settings.overhead_per_minute = calculatePerMinuteRate(
      settings.annual_overhead,
      settings.annual_hours
    );
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Converts existing business settings to ensure field names are correct
 * @param {Object} settings - The business settings to convert
 * @returns {Object} Settings with normalized field names
 */
export const normalizeBusinessSettings = (settings) => {
  if (!settings) return {};
  
  const normalized = { ...settings };
  
  // Map field names to ensure compatibility
  const fieldMappings = {
    hours_sold: 'annual_hours',
    weekly_target_profit: 'weekly_profit_target',
    default_vat_rate: 'vat_rate',
    staff_utilization_rate: 'target_staff_utilization',
    total_annual_overhead: 'annual_overhead',
    is_vat_registered: 'vat_registered'
  };
  
  // Copy values between equivalent fields if one is missing
  Object.entries(fieldMappings).forEach(([oldField, newField]) => {
    if (normalized[oldField] !== undefined && normalized[newField] === undefined) {
      normalized[newField] = normalized[oldField];
    } else if (normalized[newField] !== undefined && normalized[oldField] === undefined) {
      normalized[oldField] = normalized[newField];
    }
  });
  
  return normalized;
};

/**
 * Formats currency values for display
 * @param {number|string} value - The value to format
 * @param {boolean} includeSymbol - Whether to include the £ symbol
 * @returns {string} Formatted currency value
 */
export const formatCurrency = (value, includeSymbol = true) => {
  if (value === null || value === undefined) return includeSymbol ? '£0.00' : '0.00';
  
  const valueNum = parseFloat(value);
  
  if (isNaN(valueNum)) return includeSymbol ? '£0.00' : '0.00';
  
  return includeSymbol 
    ? `£${valueNum.toFixed(2)}`
    : valueNum.toFixed(2);
};

// For backward compatibility
export const getBusinessSettings = loadBusinessSettings;
export const refreshBusinessSettings = saveBusinessSettings;

export default {
  loadBusinessSettings,
  saveBusinessSettings,
  testBusinessSettingsAPI,
  calculatePerMinuteRate,
  calculateAnnualAmount,
  validateBusinessSettings,
  normalizeBusinessSettings,
  formatCurrency,
  // Include aliases for backward compatibility
  getBusinessSettings,
  refreshBusinessSettings
};